import type { ComponentTarget } from '../../app-tools/component-target.ts'
import { parse, refs, render } from '../../journey/template.ts'
import { PAGE_ROOT_COMPONENT } from './page-root.ts'
import { viewMatchesSurfaceId, type SightmapComponent, type SightmapDoc } from './schema.ts'
import { identityParameterNames, nonIdentityReason, type NonIdentityReason } from './identity-params.ts'
import { countOperationKey } from '../../lib/operation-timing.ts'

export type ResolutionLevel = 'definition' | 'surface' | 'ancestor' | 'leaf' | 'control'
export type Via = 'path' | 'alias' | 'historical-alias' | 'migrated' | 'legacy-flat'
export type StaleReason = 'unknown-name' | 'ambiguous-definition' | 'needs-params' | 'invalid-params'
  | 'handle-expired' | 'handle-changed' | 'anchor-mismatch' | 'no-pages'
/** `leafId`: the target's leaf definition, on page-level misses. */
export interface Miss { level: ResolutionLevel; subject: string; found: number; detail?: string; leafId?: string }
export type Resolution<N> =
  | { kind: 'unique'; hit: N; via: Via; handle: boolean }
  | { kind: 'ambiguous'; candidates: N[]; miss: Miss }
  | { kind: 'absent'; miss: Miss }
  | { kind: 'stale'; reason: StaleReason; miss: Miss }
export type Unresolved = Exclude<Resolution<unknown>, { kind: 'unique' }>
/** The map alone decides only `unique` or `stale`: `ambiguous` and `absent` are statements about pages. */
export type DefinitionResolution<N> = Extract<Resolution<N>, { kind: 'unique' | 'stale' }>

type TargetPath = NonNullable<ComponentTarget['path']>
/** A target's stored form (`reference`), its definition path and, per segment, the selectors of the alias it names.
 *  `root`: the component definition whose instances scope a nested component surface; null for a view, shared or page-root path. */
export interface CanonicalTarget {
  reference: ComponentTarget & { path: TargetPath }
  surfaceId: string
  root: SightmapComponent | null
  path: SightmapComponent[]
  constraints: (string[] | undefined)[]
}

export type MatchKind = 'exact' | 'alias'
type Stale = Extract<Unresolved, { kind: 'stale' }>

function stale(reason: StaleReason, subject: string, found: number, detail?: string): Stale {
  return { kind: 'stale', reason, miss: { level: 'definition', subject, found, ...(detail ? { detail } : {}) } }
}

function unique<N>(hit: N, via: Via = 'path'): { kind: 'unique'; hit: N; via: Via; handle: boolean } {
  return { kind: 'unique', hit, via, handle: false }
}

export function componentIdMatch(component: SightmapComponent, id: string): MatchKind | null {
  if (component.id === id) return 'exact'
  return component.aliases?.some(alias => alias.id === id) ? 'alias' : null
}

/** Rule P: compare anchored segments from the root, `exact < alias`; the first difference decides and
 *  equal sequences tie. An ancestor fixes the subtree later segments are read in. */
export function preferredCandidates<C>(candidates: C[], kinds: (candidate: C) => MatchKind[]): C[] {
  const ranked = candidates.map(candidate => ({ candidate, kinds: kinds(candidate) }))
  const compare = (a: MatchKind[], b: MatchKind[]) => {
    const i = a.findIndex((kind, index) => kind !== b[index])
    return i < 0 || i >= b.length ? 0 : a[i] === 'exact' ? -1 : 1
  }
  const best = ranked.reduce<MatchKind[] | null>((min, entry) => !min || compare(entry.kinds, min) < 0 ? entry.kinds : min, null)
  return ranked.filter(entry => best && compare(entry.kinds, best) === 0).map(entry => entry.candidate)
}

/** A component tree a surface's targets resolve in, owned by a view (null: shared components) and
 *  nested under `root` when the surface is itself a component. */
interface SurfaceScope { viewId: string | null; root: SightmapComponent | null; components: SightmapComponent[] }

function surfaceScopes(doc: SightmapDoc, surfaceId: string): SurfaceScope[] {
  // A recorded durable surface id binds onto the view occupying its route as an alias
  // (bind.ts), so a journaled target resolves here exactly as a map-native id does.
  const view = doc.views.find(view => viewMatchesSurfaceId(view, surfaceId))
  const shared: SurfaceScope = { viewId: null, root: null, components: doc.components ?? [] }
  if (view) return [{ viewId: view.id ?? null, root: null, components: view.components ?? [] }, shared]
  // Regions and dialogs can also be nested Sightmap objects rather than top-level views.
  const nested: { viewId: string | null; component: SightmapComponent; kind: MatchKind }[] = []
  for (const page of doc.views) {
    const find = (items: SightmapComponent[]): void => {
      for (const item of items) {
        const kind = componentIdMatch(item, surfaceId)
        if (kind) nested.push({ viewId: page.id ?? null, component: item, kind })
        else find(item.children ?? [])
      }
    }
    find(page.components ?? [])
  }
  return [shared, ...preferredCandidates(nested, entry => [entry.kind]).map(({ viewId, component }) =>
    ({ viewId, root: component, components: component.children ?? [] }))]
}

function scopePaths(scopes: SurfaceScope[]): SightmapComponent[][] {
  const paths: SightmapComponent[][] = []
  const visit = (components: SightmapComponent[], parents: SightmapComponent[]) => {
    for (const component of components) {
      const path = [...parents, component]
      paths.push(path)
      visit(component.children ?? [], path)
    }
  }
  for (const scope of scopes) visit(scope.components, [])
  // Page-root identity is shared by every map, including older stored maps.
  if (!paths.some(path => path.at(-1)?.id === PAGE_ROOT_COMPONENT.id)) paths.push([PAGE_ROOT_COMPONENT])
  return paths
}

export function componentPaths(doc: SightmapDoc, surfaceId: string): SightmapComponent[][] {
  return scopePaths(surfaceScopes(doc, surfaceId))
}

/** The page root is synthesized by `componentPaths`, so no Sightmap edit can address it. */
export const isPageRootPath = (path: SightmapComponent[]): boolean => path.length === 1 && path[0] === PAGE_ROOT_COMPONENT

/** Where Sightmap edits address a definition path's leaf: its owning view (null: shared) and parent. */
export function definitionLocation(doc: SightmapDoc, surfaceId: string, path: SightmapComponent[]): DefinitionResolution<{ viewId: string | null; parentId: string | null }> {
  const scope = surfaceScopes(doc, surfaceId).find(scope => scope.components.includes(path[0]!))
  if (!scope) return stale('unknown-name', `Sightmap component ${path[0]?.id}`, 0, `outside the scope of Surface ${surfaceId}`)
  return unique({ viewId: scope.viewId, parentId: path.at(-2)?.id ?? scope.root?.id ?? null })
}

/** A stage that needs exactly one match names its outcome: absent (0) or ambiguous (found N). */
export function resolutionMiss(subject: string, found: number, detail?: string): string {
  return `${subject} is ${found ? `ambiguous (found ${found})` : 'absent'}${detail ? `; ${detail}` : ''}`
}

/** The resolver's text for an unresolved outcome. A count-free stale reason states its subject; the rest name the count. */
export function describeMiss(resolution: Unresolved): string {
  const { subject, found, detail } = resolution.miss
  if (resolution.kind === 'stale' && resolution.reason !== 'unknown-name' && resolution.reason !== 'ambiguous-definition') return detail ? `${subject}: ${detail}` : subject
  return resolutionMiss(subject, found, detail)
}

/** A reference form and the definition path it names. */
interface PathMatch { reference: CanonicalTarget['reference']; definition: SightmapComponent[] }

const unparameterized = (path: TargetPath) => path.every(part => !Object.keys(part.params ?? {}).length)

function pathMatch(target: ComponentTarget, path: TargetPath, paths: SightmapComponent[][]): DefinitionResolution<PathMatch> {
  const ids = path.map(part => part.componentId)
  const kinds = (definition: SightmapComponent[]) => definition.map((part, i) => componentIdMatch(part, ids[i]!)!)
  const current = preferredCandidates(paths.filter(definition => definition.length === ids.length
    && definition.every((part, i) => componentIdMatch(part, ids[i]!))), kinds)
  if (current.length > 1) return stale('ambiguous-definition', 'Sightmap target hierarchy', current.length)
  if (current.length) return unique({ reference: { ...target, path }, definition: current[0]! }, kinds(current[0]!).includes('alias') ? 'alias' : 'path')

  // Consolidation records complete unparameterized historical paths, so surviving
  // wrappers elsewhere in the map cannot invalidate a deliberately moved alias.
  const historical = unparameterized(path) ? paths.filter(definition => definition.at(-1)?.aliases?.some(alias =>
    alias.path && alias.path.length === ids.length && alias.path.every((id, i) => id === ids[i]))) : []
  if (historical.length === 1) {
    const definition = historical[0]!
    return unique({ reference: { ...target, path: definition.map((part, index) => ({
      componentId: index === definition.length - 1 ? ids.at(-1)! : part.id!,
    })) }, definition }, 'historical-alias')
  }
  return migratedPath(target, path, paths)
}

function migratedPath(target: ComponentTarget, path: TargetPath, paths: SightmapComponent[][]): DefinitionResolution<PathMatch> {
  const leaf = path.at(-1)
  // Retain every surviving ancestor and every parameter constraint. Only obsolete,
  // unparameterized wrappers may disappear when the map reorganizes a definition.
  const anchors = path.filter(part => Object.keys(part.params ?? {}).length
    || paths.some(definition => componentIdMatch(definition.at(-1)!, part.componentId)))
  let unidentified = 0
  const candidates = paths.filter(definition => leaf && componentIdMatch(definition.at(-1)!, leaf.componentId)
    && definition.every(component => component.id)).flatMap(definition => {
    let next = 0
    const kinds: MatchKind[] = []
    const introduced: SightmapComponent[] = []
    const migrated = definition.map(component => {
      const anchor = anchors[next]
      const kind = anchor && componentIdMatch(component, anchor.componentId)
      if (!kind) {
        introduced.push(component)
        return { componentId: component.id! }
      }
      next++
      kinds.push(kind)
      return anchor
    })
    if (next !== anchors.length) return []
    // Newly introduced record containers need identifying values; never choose one implicitly.
    // Anchored parts keep their stored params, as on a current path: the page stage decides.
    if (introduced.some(component => identityParameterNames(component).length)) {
      unidentified++
      return []
    }
    return [{ definition, migrated, kinds }]
  })
  if (!candidates.length && unidentified) {
    return stale('needs-params', 'Sightmap target hierarchy needs identifying parameters', 0,
      `${unidentified} current path(s) have a record component whose identifying parameters the reference does not supply; update its reference to the current Sightmap`)
  }
  const chosen = preferredCandidates(candidates, candidate => candidate.kinds)
  if (chosen.length !== 1) return stale(chosen.length ? 'ambiguous-definition' : 'unknown-name', 'Sightmap target hierarchy', chosen.length, 'update its reference to the current Sightmap')
  return unique({ reference: { ...target, path: chosen[0]!.migrated }, definition: chosen[0]!.definition }, 'migrated')
}

/** Read old flat IDs only when the map establishes one complete, unparameterized path. */
function legacyFlatMatch(target: ComponentTarget, paths: SightmapComponent[][]): DefinitionResolution<PathMatch> {
  const controlId = target.controlId
  const subject = `Legacy target needs a complete Sightmap reference: control ${controlId} in the Sightmap of Surface ${target.surfaceId}`
  const chosen = controlId ? preferredCandidates(paths.filter(path => componentIdMatch(path.at(-1)!, controlId)), path => [componentIdMatch(path.at(-1)!, controlId)!]) : []
  if (chosen.length !== 1) {
    return stale(chosen.length ? 'ambiguous-definition' : 'unknown-name', subject, chosen.length,
      chosen.length ? 'identify the hierarchy and record parameters' : 're-author it against the current Sightmap')
  }
  const definition = chosen[0]!
  if (definition.some(component => identityParameterNames(component).length || !component.id)) return stale('needs-params', subject, 1, 'identify the hierarchy and record parameters')
  return unique({ reference: { surfaceId: target.surfaceId, path: definition.map((component, i) => ({
    componentId: i === definition.length - 1 ? controlId! : component.id!,
  })) }, definition }, 'legacy-flat')
}

/** The flat targets a recorded control id names: on its surface, or on every view when no surface was recorded. */
export function flatTargetCandidates(doc: SightmapDoc, controlId: string, surfaceId?: string): ComponentTarget[] {
  return surfaceId ? [{ surfaceId, controlId }] : doc.views.flatMap(view => view.id ? [{ surfaceId: view.id, controlId }] : [])
}

/** Every `unique` definition the flat targets resolve to, without dedupe. A caller accepts exactly one. */
export function flatTargetDefinitions(doc: SightmapDoc, targets: ComponentTarget[]): CanonicalTarget[] {
  return targets.flatMap(target => {
    const resolution = resolveDefinition(target, doc)
    return resolution.kind === 'unique' ? [resolution.hit] : []
  })
}

/** Map-level canonicalization: the current definition path a target names, its stored reference form,
 *  and the alias constraints its page match applies. A map defect is `stale`. */
export function resolveDefinition(target: ComponentTarget, doc: SightmapDoc): DefinitionResolution<CanonicalTarget> {
  const scopes = surfaceScopes(doc, target.surfaceId)
  const paths = scopePaths(scopes)
  const match = target.path ? pathMatch(target, target.path, paths) : legacyFlatMatch(target, paths)
  // Only the shared scope: the surface names no view and no component.
  if (match.kind !== 'unique') return scopes.length < 2 && match.reason === 'unknown-name' ? { ...match, miss: { ...match.miss, level: 'surface' } } : match
  const { reference, definition } = match.hit
  const view = doc.views.find(view => viewMatchesSurfaceId(view, target.surfaceId))
  const root = scopes.find(scope => scope.root && scope.components.includes(definition[0]!))?.root ?? null
  return unique({
    reference,
    surfaceId: view?.id ?? root?.id ?? target.surfaceId,
    root,
    path: definition,
    constraints: definition.map((component, i) => {
      if (componentIdMatch(component, reference.path[i]!.componentId) !== 'alias') return undefined
      const alias = component.aliases!.find(alias => alias.id === reference.path[i]!.componentId)!
      return Array.isArray(alias.selector) ? alias.selector : [alias.selector]
    }),
  }, match.via)
}

/** The definition an authored target names: its Surface exists in the map, the map resolver finds one
 *  definition for it and no key is stale for its role. Otherwise the problem, phrased for the authoring model. */
export function authoredTargetDefinition(target: ComponentTarget, doc: SightmapDoc | null, collection: boolean): { definition: SightmapComponent[] } | { problem: string } {
  if (!doc) return { problem: 'The task has no Sightmap, so no component target can be verified; use a discovered label instead' }
  // The shared-components scope is always present; any further scope is the Surface's view or component.
  if (surfaceScopes(doc, target.surfaceId).length < 2) return { problem: `Surface ${target.surfaceId} is not a page or component in the Sightmap; copy a surfaceId shown for the element` }
  const problem = (miss: Unresolved) => ({ problem: `Sightmap target ${JSON.stringify(target)}: ${describeMiss(miss)}` })
  const resolution = resolveDefinition(target, doc)
  if (resolution.kind !== 'unique') return problem(resolution)
  const miss = nonIdentityKeyStale(resolution.hit, collection)
  return miss ? problem(miss) : { definition: resolution.hit.path }
}

/** A key's static non-identity reason that a target of this role may not carry; a collection filters `mutable state`. */
export function forbiddenReason(definition: SightmapComponent, key: string, collection: boolean): NonIdentityReason | null {
  const reason = nonIdentityReason(definition, key)
  return collection && reason === 'mutable state' ? null : reason
}

/** An observation identity target's `mutable state` key is dropped, not stale: a recorded state of the bound
 *  element, never an author's selection. */
const dropsKey = (target: ComponentTarget, reason: NonIdentityReason) => target.paramsSource === 'observation' && reason === 'mutable state'

function nonIdentityStale(key: string, reason: NonIdentityReason, part: TargetPath[number], definition: SightmapComponent): Stale {
  return stale('invalid-params', `${key} is ${reason} of Sightmap component ${part.componentId} (${definition.name}), not instance identity`, 0)
}

/** `targetParams`' reason rule at map level, for authoring and binding: the stale outcome resolution gives the
 *  target's first non-identity key, or null. Variables are not substituted. */
export function nonIdentityKeyStale(hit: CanonicalTarget, collection: boolean): Stale | null {
  for (const [i, part] of hit.reference.path.entries()) {
    for (const key of Object.keys(part.params ?? {})) {
      const reason = forbiddenReason(hit.path[i]!, key, collection)
      if (reason && !dropsKey(hit.reference, reason)) return nonIdentityStale(key, reason, part, hit.path[i]!)
    }
  }
  return null
}

/** Whether `paramsSource` decides a resolved target's outcome beyond `$` reading: a `mutable state` key is dropped
 *  from an observation identity target and stales an authored one. */
export function sourceDecidesKeys(hit: CanonicalTarget): boolean {
  return hit.reference.path.some((part, i) => Object.keys(part.params ?? {}).some(key => nonIdentityReason(hit.path[i]!, key) === 'mutable state'))
}

/** One path part's parameter filter, variables substituted. A key with a static non-identity reason is
 *  stale, except `mutable state`: a count filter keeps it, an observation identity target drops it (`dropsKey`). */
export function targetParams(target: ComponentTarget, part: TargetPath[number], definition: SightmapComponent,
  variables: Map<string, string>, collection: boolean): DefinitionResolution<Record<string, string>> {
  const observation = target.paramsSource === 'observation'
  // Target filters must never treat an unresolved variable as a literal that matches nothing.
  const missing = observation ? undefined : Object.entries(part.params ?? {}).flat()
    .map(text => refs(parse(text, 'target')).find(name => variables.get(name) === undefined)).find(name => name !== undefined)
  if (missing !== undefined) return stale('invalid-params', 'Missing target parameter variable', 0, missing)
  const expected: Record<string, string> = {}
  for (const [key, value] of Object.entries(part.params ?? {})) {
    const property = observation ? key : render(parse(key, 'target'), variables)
    if (Object.prototype.hasOwnProperty.call(expected, property)) return stale('invalid-params', 'Duplicate target property', 0, property)
    expected[property] = observation ? value : render(parse(value, 'target'), variables)
  }
  const declared = new Set([...(definition.params ?? []), ...(definition.properties ?? []).map(p => p.name)])
  if (Object.keys(expected).some(key => !declared.has(key))) return stale('invalid-params', `Unknown Sightmap parameter on ${part.componentId}`, 0)
  for (const key of Object.keys(expected)) {
    const reason = forbiddenReason(definition, key, collection)
    if (!reason) continue
    if (dropsKey(target, reason)) {
      delete expected[key]
      continue
    }
    // Sizes the stored targets this rule turns stale (pre-#6093 saves, definitions reclassified after the write).
    countOperationKey(`sightmap.identity.stale-key.${observation ? 'observation' : 'authored'}.${reason.replace(/ /g, '-')}`, definition, JSON.stringify([key, expected[key]]))
    return nonIdentityStale(key, reason, part, definition)
  }
  return unique(expected)
}

/** Retained filter expressions, including variable dependency, independent of component addresses. */
export function targetFilters(target: ComponentTarget, doc: SightmapDoc, variables: Map<string, string>, collection: boolean): DefinitionResolution<Record<string, string>[]> {
  const resolution = resolveDefinition(target, doc)
  if (resolution.kind !== 'unique') return resolution
  const { reference, path } = resolution.hit
  const observation = reference.paramsSource === 'observation'
  // Observation parameters are literal even when their text resembles a variable reference.
  const expression = (value: string) => JSON.stringify([value, !observation && refs(parse(value, 'target')).length > 0])
  const filters: Record<string, string>[] = []
  for (const [i, part] of reference.path.entries()) {
    const retained = targetParams(reference, part, path[i]!, variables, collection)
    if (retained.kind !== 'unique') return retained
    const params = Object.fromEntries(Object.entries(part.params ?? {}).filter(([key]) =>
      Object.prototype.hasOwnProperty.call(retained.hit, observation ? key : render(parse(key, 'target'), variables)))
      .map(([key, value]) => [expression(key), expression(value)]))
    if (Object.keys(params).length) filters.push(params)
  }
  return unique(filters, resolution.via)
}
